import ToggleTasks from "@/components/SingleLineTask/toggleTasks.component";
import SingleTaskViewSheet from "@/components/SingleTaskViewSheet/singleTaskViewSheet.component";
import CreateTaskTags from "@/components/CreateTaskTags/createTaskTags.component";
import CollaboratorPicker from "@/components/CollaboratorPicker/collaboratorPicker.component";
import AssigneePicker from "@/components/PersonPickerDropdown/assigneePicker.component";
import DueDatePicker from "@/components/DueDatePicker/dueDatePicker.component";
import { DeleteTaskDialog } from "@/components/Dialogs/deleteTaskDialog.component";
import { useMediaQuery } from "@/hooks/AppContext/mediaQuery";
import {
  fetchTask,
  sendDeleteTasks,
  updateTask,
  updateTaskStatus,
} from "@/models/tasks.model";
import { AppContext } from "@/services/AppContext/appContext";
import {
  ResizablePanel,
  ResizablePanelGroup,
} from "@/shadcn/components/ui/resizable";
import { ScrollArea } from "@/shadcn/components/ui/scroll-area";
import { Button } from "@/shadcn/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shadcn/components/ui/dialog";
import { Checkbox } from "@/shadcn/components/ui/checkbox";
import { Collaborator, EditedTask, NewTaskTag, Tag, BasicTask } from "@/types";
import {
  formatDueDate,
  genericFailureToast,
  genericSuccessToast,
  getNewTaskTagByID,
} from "@/utils/utils";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Calendar,
  CheckCircle,
  Circle,
  Square,
  Tags,
  Trash,
  User,
  Users,
} from "lucide-react";
import { useParams } from "react-router";
import Swal from "sweetalert2";

const AllTaskView: React.FC = () => {
  const [tasks, setTasks] = useState<
    | {
        overDue: BasicTask[];
        dueToday: BasicTask[];
        dueLater: BasicTask[];
        noDueDate: BasicTask[];
      }
    | undefined
  >(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const {
    userTasks,
    initialGetUserTasks,
    forceFetchAll,
    allAppUsers,
    userTags,
  } = useContext(AppContext);
  // const controllerRef = useRef(new AbortController());

  const { taskID } = useParams();

  const [viewingTaskID, setViewingTaskID] = useState<number | null>(null);
  const [selectedTaskIDs, setSelectedTaskIDs] = useState<number[]>([]);
  const [bulkActionSubmitting, setBulkActionSubmitting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isBulkTagsDialogOpen, setIsBulkTagsDialogOpen] = useState(false);
  const [isBulkCollaboratorsDialogOpen, setIsBulkCollaboratorsDialogOpen] =
    useState(false);
  const [isBulkAssigneeDialogOpen, setIsBulkAssigneeDialogOpen] =
    useState(false);
  const [isBulkDueDateDialogOpen, setIsBulkDueDateDialogOpen] = useState(false);
  const [bulkTags, setBulkTags] = useState<NewTaskTag[]>([]);
  const [bulkTagAction, setBulkTagAction] = useState<"add" | "remove">("add");
  const [bulkCollaborators, setBulkCollaborators] = useState<number[]>([]);
  const [bulkCollaboratorAction, setBulkCollaboratorAction] = useState<
    "add" | "remove"
  >("add");
  const [bulkAssignee, setBulkAssignee] = useState<number | null>(null);
  const [bulkDueDateAction, setBulkDueDateAction] = useState<"set" | "clear">(
    "set",
  );
  const [bulkDueDate, setBulkDueDate] = useState<Date | null | undefined>(
    undefined,
  );
  const [bulkEditVersion, setBulkEditVersion] = useState(0);
  const [
    bulkCollaboratorsDialogContainer,
    setBulkCollaboratorsDialogContainer,
  ] = useState<HTMLDivElement | null>(null);
  const bulkCollaboratorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!taskID) {
      setViewingTaskID(null);
      return;
    }
    // check if number
    const taskIDNum = Number(taskID);
    if (isNaN(taskIDNum)) {
      console.error("Task ID is not a number");
      Swal.fire({
        title: "Error",
        text: "Task ID is not a number",
        icon: "error",
      });
      setViewingTaskID(null);
      return;
    }
    setViewingTaskID(taskIDNum);
  }, [taskID]);

  useEffect(() => {
    // TODO: Must be a better way of doing this
    // This ideally shouldn't need to happen each render
    initialGetUserTasks({ force: true }).then(() => {
      setLoading(false);
    });

    // return () => {
    //   controllerRef.current.abort();
    // };
  }, []);

  useEffect(() => {
    const tasksCategorized = {
      overDue: [] as BasicTask[],
      dueToday: [] as BasicTask[],
      dueLater: [] as BasicTask[],
      noDueDate: [] as BasicTask[],
    };

    const todayDay = new Date();
    todayDay.setHours(0, 0, 0, 0);

    userTasks.forEach((task) => {
      if (task.status === "complete") return;

      if (!task.due_date) {
        tasksCategorized.noDueDate.push(task);
        return;
      }

      const dueDate = new Date(task.due_date);
      dueDate.setHours(0, 0, 0, 0);

      if (dueDate < todayDay) {
        tasksCategorized.overDue.push(task);
      } else if (dueDate.toDateString() === todayDay.toDateString()) {
        tasksCategorized.dueToday.push(task);
      } else {
        tasksCategorized.dueLater.push(task);
      }
    });

    // sort by due date, oldest first
    Object.values(tasksCategorized).forEach((taskList) =>
      taskList.sort(
        (a, b) =>
          new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime(),
      ),
    );

    setTasks(tasksCategorized);
  }, [userTasks]);

  const availableTasks = useMemo(() => {
    if (!tasks) {
      return [] as BasicTask[];
    }
    return [
      ...tasks.overDue,
      ...tasks.dueToday,
      ...tasks.dueLater,
      ...tasks.noDueDate,
    ];
  }, [tasks]);

  const selectedTaskIdSet = useMemo(
    () => new Set(selectedTaskIDs),
    [selectedTaskIDs],
  );
  const selectedTasks = useMemo(
    () => availableTasks.filter((task) => selectedTaskIdSet.has(task.id)),
    [availableTasks, selectedTaskIdSet],
  );
  const selectedTaskCount = selectedTaskIDs.length;

  useEffect(() => {
    if (!availableTasks.length) {
      if (selectedTaskIDs.length) {
        setSelectedTaskIDs([]);
      }
      return;
    }

    const validIds = new Set(availableTasks.map((task) => task.id));
    setSelectedTaskIDs((prev) => prev.filter((id) => validIds.has(id)));
  }, [availableTasks]);

  const handleToggleTaskSelection = useCallback(
    (taskID: number, selected: boolean) => {
      setSelectedTaskIDs((prev) => {
        const next = new Set(prev);
        if (selected) {
          next.add(taskID);
        } else {
          next.delete(taskID);
        }
        return Array.from(next);
      });
    },
    [],
  );

  const clearSelection = useCallback(() => {
    setSelectedTaskIDs([]);
  }, []);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!availableTasks.length) {
        return;
      }
      setSelectedTaskIDs(checked ? availableTasks.map((task) => task.id) : []);
    },
    [availableTasks],
  );

  const buildEditedTask = useCallback(
    (taskID: number, updates: Partial<EditedTask>): EditedTask => ({
      id: taskID,
      status: undefined,
      title: undefined,
      due_date: undefined,
      input: undefined,
      collaborators: undefined,
      tags: undefined,
      assigned_to: undefined,
      parent_id: undefined,
      ...updates,
    }),
    [],
  );

  const findTagByName = useCallback(
    (tagName: string, color?: string): NewTaskTag | undefined => {
      const availableTags = userTags ?? [];
      for (const category of availableTags) {
        const matched = category.tags.find((tag) => {
          if (color) {
            return tag.name === tagName && tag.color === color;
          }
          return tag.name === tagName;
        });
        if (matched) {
          return {
            category_id: category.id,
            tag: matched.name,
            color: matched.color,
          };
        }
      }
      return undefined;
    },
    [userTags],
  );

  const normalizeTaskTags = useCallback(
    (taskTags: Tag[]): NewTaskTag[] => {
      const availableTags = userTags ?? [];
      return taskTags
        .map((tag) => {
          const tagRecord = tag as Tag & {
            tag?: string;
            category_id?: number;
          };
          const tagName = tagRecord.tag ?? tag.name;
          const tagColor = tag.color;
          if (
            typeof tagRecord.category_id === "number" &&
            typeof tagRecord.tag === "string"
          ) {
            return {
              category_id: tagRecord.category_id,
              tag: tagRecord.tag,
              color: tagColor,
            } as NewTaskTag;
          }
          if (availableTags.length) {
            const byId = getNewTaskTagByID(tag.id, availableTags);
            if (byId) {
              return byId;
            }
            if (tagName) {
              return findTagByName(tagName, tagColor);
            }
          }
          return undefined;
        })
        .filter((tag): tag is NewTaskTag => Boolean(tag));
    },
    [findTagByName, userTags],
  );

  const tagKey = useCallback(
    (tag: NewTaskTag) => `${tag.category_id}:${tag.tag}`,
    [],
  );

  const mergeTags = useCallback(
    (existing: NewTaskTag[], incoming: NewTaskTag[]) => {
      const map = new Map(existing.map((tag) => [tagKey(tag), tag]));
      incoming.forEach((tag) => map.set(tagKey(tag), tag));
      return Array.from(map.values());
    },
    [tagKey],
  );

  const removeTags = useCallback(
    (existing: NewTaskTag[], remove: NewTaskTag[]) => {
      const removeKeys = new Set(remove.map((tag) => tagKey(tag)));
      return existing.filter((tag) => !removeKeys.has(tagKey(tag)));
    },
    [tagKey],
  );

  const mergeCollaborators = useCallback(
    (existing: number[], incoming: number[], action: "add" | "remove") => {
      const set = new Set(existing);
      incoming.forEach((id) => {
        if (action === "add") {
          set.add(id);
        } else {
          set.delete(id);
        }
      });
      return Array.from(set);
    },
    [],
  );

  const resolveTaskCollaborators = useCallback(async (task: BasicTask) => {
    const taskWithCollaborators = task as BasicTask & {
      collaborators?: number[];
    };
    if (Array.isArray(taskWithCollaborators.collaborators)) {
      return taskWithCollaborators.collaborators;
    }

    const response = await fetchTask({ taskID: task.id });
    if (response.rStatus === "success") {
      return response.task.collaborators.map((collaborator) => collaborator.id);
    }
    return null;
  }, []);

  const bulkCollaboratorSelections = useMemo<Collaborator[]>(
    () =>
      allAppUsers
        .filter((user) => bulkCollaborators.includes(user.id))
        .map((user) => ({
          id: user.id,
          full_name: `${user.first_name} ${user.surname}`.trim(),
          email: user.email,
        })),
    [allAppUsers, bulkCollaborators],
  );

  const runBulkTaskUpdate = useCallback(
    async (
      createTaskUpdate: (task: BasicTask) => EditedTask,
      successMessage: string,
    ) => {
      if (bulkActionSubmitting) return;
      if (!selectedTasks.length) {
        genericFailureToast("No tasks selected");
        return;
      }

      setBulkActionSubmitting(true);
      try {
        const results = await Promise.all(
          selectedTasks.map((task) =>
            updateTask({ task: createTaskUpdate(task) }),
          ),
        );
        const failedCount = results.filter(
          (result) => result.rStatus === "error",
        ).length;
        if (failedCount > 0) {
          genericFailureToast(
            `Failed to update ${failedCount} of ${selectedTasks.length} tasks`,
          );
        } else {
          genericSuccessToast(successMessage);
        }
        clearSelection();
        await forceFetchAll();
        setBulkEditVersion((prev) => prev + 1);
      } finally {
        setBulkActionSubmitting(false);
      }
    },
    [bulkActionSubmitting, clearSelection, forceFetchAll, selectedTasks],
  );

  const runBulkStatusUpdate = useCallback(
    async (status: "complete" | "incomplete") => {
      if (bulkActionSubmitting) return;
      if (!selectedTasks.length) {
        genericFailureToast("No tasks selected");
        return;
      }

      setBulkActionSubmitting(true);
      try {
        const results = await Promise.all(
          selectedTasks.map((task) =>
            updateTaskStatus({ taskID: task.id, status }),
          ),
        );
        const failedCount = results.filter(
          (result) => result.rStatus === "error",
        ).length;
        if (failedCount > 0) {
          genericFailureToast(
            `Failed to update ${failedCount} of ${selectedTasks.length} tasks`,
          );
        } else {
          genericSuccessToast(
            status === "complete"
              ? "Tasks marked complete"
              : "Tasks marked incomplete",
          );
        }
        clearSelection();
        await forceFetchAll();
        setBulkEditVersion((prev) => prev + 1);
      } finally {
        setBulkActionSubmitting(false);
      }
    },
    [bulkActionSubmitting, clearSelection, forceFetchAll, selectedTasks],
  );

  const closeBulkTagsDialog = useCallback(() => {
    setIsBulkTagsDialogOpen(false);
    setBulkTags([]);
    setBulkTagAction("add");
  }, []);

  const closeBulkCollaboratorsDialog = useCallback(() => {
    setIsBulkCollaboratorsDialogOpen(false);
    setBulkCollaborators([]);
    setBulkCollaboratorAction("add");
  }, []);

  const closeBulkAssigneeDialog = useCallback(() => {
    setIsBulkAssigneeDialogOpen(false);
    setBulkAssignee(null);
  }, []);

  const closeBulkDueDateDialog = useCallback(() => {
    setIsBulkDueDateDialogOpen(false);
    setBulkDueDateAction("set");
    setBulkDueDate(undefined);
  }, []);

  const handleBulkTagsApply = useCallback(async () => {
    if (!bulkTags.length) {
      genericFailureToast("Select at least one tag");
      return;
    }
    if (!userTags || userTags.length === 0) {
      genericFailureToast("Tags are still loading");
      return;
    }
    await runBulkTaskUpdate(
      (task) => {
        const existingTags = normalizeTaskTags(task.tags ?? []);
        const nextTags =
          bulkTagAction === "add"
            ? mergeTags(existingTags, bulkTags)
            : removeTags(existingTags, bulkTags);
        return buildEditedTask(task.id, { tags: nextTags });
      },
      bulkTagAction === "add"
        ? "Tags added to tasks"
        : "Tags removed from tasks",
    );
    closeBulkTagsDialog();
  }, [
    bulkTags,
    userTags,
    runBulkTaskUpdate,
    normalizeTaskTags,
    bulkTagAction,
    mergeTags,
    removeTags,
    buildEditedTask,
    closeBulkTagsDialog,
  ]);

  const handleBulkCollaboratorsApply = useCallback(async () => {
    if (!bulkCollaborators.length) {
      genericFailureToast("Select at least one collaborator");
      return;
    }
    if (!selectedTasks.length) {
      genericFailureToast("No tasks selected");
      return;
    }
    if (bulkActionSubmitting) return;

    setBulkActionSubmitting(true);
    try {
      const results = await Promise.all(
        selectedTasks.map(async (task) => {
          const existingCollaborators = await resolveTaskCollaborators(task);
          if (!existingCollaborators) {
            return { rStatus: "error", message: "collaborators_unavailable" };
          }
          const nextCollaborators = mergeCollaborators(
            existingCollaborators,
            bulkCollaborators,
            bulkCollaboratorAction,
          );
          return updateTask({
            task: buildEditedTask(task.id, {
              collaborators: nextCollaborators,
            }),
          });
        }),
      );
      const failedCount = results.filter(
        (result) => result.rStatus === "error",
      ).length;
      if (failedCount > 0) {
        genericFailureToast(
          `Failed to update ${failedCount} of ${selectedTasks.length} tasks`,
        );
      } else {
        genericSuccessToast(
          bulkCollaboratorAction === "add"
            ? "Collaborators added to tasks"
            : "Collaborators removed from tasks",
        );
      }
      clearSelection();
      await forceFetchAll();
      setBulkEditVersion((prev) => prev + 1);
    } finally {
      setBulkActionSubmitting(false);
    }
    closeBulkCollaboratorsDialog();
  }, [
    bulkCollaborators,
    bulkCollaboratorAction,
    bulkActionSubmitting,
    buildEditedTask,
    clearSelection,
    closeBulkCollaboratorsDialog,
    forceFetchAll,
    mergeCollaborators,
    resolveTaskCollaborators,
    selectedTasks,
  ]);

  const handleBulkAssigneeApply = useCallback(async () => {
    if (!bulkAssignee) {
      genericFailureToast("Select an assignee");
      return;
    }
    await runBulkTaskUpdate(
      (task) => buildEditedTask(task.id, { assigned_to: bulkAssignee }),
      "Tasks assigned",
    );
    closeBulkAssigneeDialog();
  }, [
    bulkAssignee,
    runBulkTaskUpdate,
    buildEditedTask,
    closeBulkAssigneeDialog,
  ]);

  const handleBulkDueDateApply = useCallback(async () => {
    if (bulkDueDateAction === "set") {
      if (!bulkDueDate) {
        genericFailureToast("Pick a due date");
        return;
      }
      const formatted = formatDueDate(bulkDueDate);
      if (!formatted) {
        genericFailureToast("Invalid due date");
        return;
      }
      await runBulkTaskUpdate(
        (task) => buildEditedTask(task.id, { due_date: formatted }),
        "Due dates updated",
      );
    } else {
      await runBulkTaskUpdate(
        (task) => buildEditedTask(task.id, { due_date: null }),
        "Due dates cleared",
      );
    }
    closeBulkDueDateDialog();
  }, [
    bulkDueDateAction,
    bulkDueDate,
    runBulkTaskUpdate,
    buildEditedTask,
    closeBulkDueDateDialog,
  ]);

  const handleBulkTagChange = useCallback((newTag: NewTaskTag) => {
    setBulkTags((prev) => {
      const exists = prev.some(
        (tag) =>
          tag.tag === newTag.tag && tag.category_id === newTag.category_id,
      );
      if (exists) {
        return prev.filter(
          (tag) =>
            !(tag.tag === newTag.tag && tag.category_id === newTag.category_id),
        );
      }
      return [...prev, newTag];
    });
  }, []);

  const handleBulkCollaboratorToggle = useCallback((id: number) => {
    setBulkCollaborators((prev) =>
      prev.includes(id)
        ? prev.filter((collab) => collab !== id)
        : [...prev, id],
    );
  }, []);

  async function handleBulkDeleteTasks() {
    if (!selectedTasks.length) {
      genericFailureToast("No tasks selected");
      return;
    }

    const selectedIds = selectedTasks.map((task) => task.id);
    const res = await sendDeleteTasks({ taskIDs: selectedIds });
    setIsDeleteDialogOpen(false);
    if (res.rStatus === "error") {
      console.error("Error deleting tasks", res.message);
      genericFailureToast("Error deleting tasks");
      return;
    }

    genericSuccessToast("Tasks deleted successfully");
    clearSelection();
    await forceFetchAll();
    setBulkEditVersion((prev) => prev + 1);
  }

  const isBulkEditing =
    bulkActionSubmitting ||
    isBulkTagsDialogOpen ||
    isBulkCollaboratorsDialogOpen ||
    isBulkAssigneeDialogOpen ||
    isBulkDueDateDialogOpen ||
    isDeleteDialogOpen ||
    selectedTaskCount > 0;

  const allTasksSelected =
    availableTasks.length > 0 && selectedTaskCount === availableTasks.length;
  const selectAllState =
    selectedTaskCount === 0 ? false : allTasksSelected ? true : "indeterminate";

  const isNotSmall = useMediaQuery("sm");

  // TODO: loader states managed by context?
  return (
    <ResizablePanelGroup
      id={"panel-group-all-tasks-view"}
      direction="horizontal"
      className="flex flex-col h-full relative"
    >
      {(isNotSmall || (!isNotSmall && !viewingTaskID)) && (
        <ResizablePanel
          id="panel-all-tasks-view"
          order={1}
          className="h-full flex flex-col"
        >
          {/* TODO:  set to a min width of 600px */}
          <h1 className="text-xl md:text-3xl font-bold text-center text-primary pt-0 md:pt-3">
            My Tasks
          </h1>
          {selectedTaskCount > 0 && (
            <div className="flex items-center justify-between px-4 py-2">
              <div className="text-sm text-muted-foreground">
                {selectedTaskCount} selected
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={bulkActionSubmitting}>
                    Bulk Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="gap-1 flex flex-col">
                  <DropdownMenuItem asChild>
                    <Button
                      variant="outline"
                      className="w-full cursor-pointer"
                      onClick={clearSelection}
                      size={"sm"}
                    >
                      <Square />
                      Unselect All
                    </Button>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => setIsBulkTagsDialogOpen(true)}
                  >
                    <Tags />
                    Add or Remove Tag
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => setIsBulkCollaboratorsDialogOpen(true)}
                  >
                    <Users />
                    Add or Remove Collaborator
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => setIsBulkAssigneeDialogOpen(true)}
                  >
                    <User />
                    Assign To
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => setIsBulkDueDateDialogOpen(true)}
                  >
                    <Calendar />
                    Change Due Date
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      void runBulkStatusUpdate("complete");
                    }}
                  >
                    <CheckCircle />
                    Mark Complete
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      void runBulkStatusUpdate("incomplete");
                    }}
                  >
                    <Circle />
                    Mark Incomplete
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Button
                      variant="outline"
                      className="w-full cursor-pointer"
                      onClick={() => {
                        setIsDeleteDialogOpen(true);
                      }}
                      size={"sm"}
                    >
                      <Trash />
                      Bulk Delete
                    </Button>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          <ScrollArea className="flex-grow m-4 mr-0 pr-4 overflow-auto">
            {/* Headers */}
            {/*     grid-template-columns: repeat(14, minmax(0, 1fr));*/}
            <div className="hidden md:grid grid-cols-14 items-center font-medium">
              <div className="col-span-1 px-2 flex items-center">
                <Checkbox
                  checked={selectAllState}
                  onCheckedChange={(value) => handleSelectAll(value === true)}
                  disabled={!availableTasks.length}
                  aria-label="Select all tasks"
                />
              </div>
              <div className="col-span-3 px-2">Title</div>
              <div className="col-span-2 px-2">Assignee</div>
              <div className="col-span-2 px-2">Projects</div>
              <div className="col-span-2 px-2">Tags</div>
              <div className="col-span-2 px-2">Due Date</div>
              <div className="col-span-2 text-right px-2">Timer</div>
            </div>
            <div className="grid md:hidden grid-cols-14 items-center font-medium">
              <div className="col-span-2 px-2 flex items-center">
                <Checkbox
                  checked={selectAllState}
                  onCheckedChange={(value) => handleSelectAll(value === true)}
                  disabled={!availableTasks.length}
                  aria-label="Select all tasks"
                />
              </div>
              <div className="col-span-8 px-2">Title</div>
              <div className="col-span-4 px-2">Due</div>
            </div>
            <hr />
            {/* wait until tasks are set, to render children */}
            {tasks === undefined ? null : (
              <>
                {/* Overdue */}
                <ToggleTasks
                  type="Overdue"
                  tasks={tasks.overDue}
                  showRowsInitial={
                    // initially show rows if there are overdue tasks
                    tasks.overDue.length > 0
                  }
                  selectedTaskIds={selectedTaskIdSet}
                  onToggleTaskSelection={handleToggleTaskSelection}
                  inlineEditingDisabled={isBulkEditing}
                  bulkEditVersion={bulkEditVersion}
                />
                {/* Due Today */}
                <ToggleTasks
                  type="Due Today"
                  tasks={tasks.dueToday}
                  showRowsInitial={
                    // initially show rows if there are no overdue tasks and there are tasks due today
                    tasks.overDue.length === 0 && tasks.dueToday.length > 0
                  }
                  selectedTaskIds={selectedTaskIdSet}
                  onToggleTaskSelection={handleToggleTaskSelection}
                  inlineEditingDisabled={isBulkEditing}
                  bulkEditVersion={bulkEditVersion}
                />
                {/* Due Later */}
                <ToggleTasks
                  type="Due Later"
                  tasks={tasks.dueLater}
                  showRowsInitial={
                    // initially show rows if there are no overdue tasks and no tasks due today
                    tasks.overDue.length === 0 && tasks.dueToday.length === 0
                  }
                  selectedTaskIds={selectedTaskIdSet}
                  onToggleTaskSelection={handleToggleTaskSelection}
                  inlineEditingDisabled={isBulkEditing}
                  bulkEditVersion={bulkEditVersion}
                />
                <ToggleTasks
                  type="No Due Date"
                  tasks={tasks.noDueDate}
                  showRowsInitial={
                    // initially show rows if there are no overdue tasks and no tasks due today
                    tasks.overDue.length === 0 && tasks.dueToday.length === 0
                  }
                  selectedTaskIds={selectedTaskIdSet}
                  onToggleTaskSelection={handleToggleTaskSelection}
                  inlineEditingDisabled={isBulkEditing}
                  bulkEditVersion={bulkEditVersion}
                />
              </>
            )}
          </ScrollArea>
        </ResizablePanel>
      )}

      {viewingTaskID && <SingleTaskViewSheet taskID={viewingTaskID} />}
      <Dialog
        open={isBulkTagsDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeBulkTagsDialog();
            return;
          }
          setIsBulkTagsDialogOpen(true);
        }}
      >
        <DialogContent
          ref={setBulkCollaboratorsDialogContainer}
          className="sm:max-w-[520px]"
        >
          <DialogHeader>
            <DialogTitle>Bulk Tags</DialogTitle>
            <DialogDescription>
              Apply tags to {selectedTaskCount} selected tasks.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={bulkTagAction === "add" ? "default" : "outline"}
                onClick={() => setBulkTagAction("add")}
                disabled={bulkActionSubmitting}
              >
                Add
              </Button>
              <Button
                type="button"
                variant={bulkTagAction === "remove" ? "default" : "outline"}
                onClick={() => setBulkTagAction("remove")}
                disabled={bulkActionSubmitting}
              >
                Remove
              </Button>
            </div>
            <CreateTaskTags
              id="bulk-tags"
              taskTags={bulkTags}
              handleFieldTags={handleBulkTagChange}
              createTagAllowed={false}
              modal={true}
            />
            <div className="text-xs text-muted-foreground">
              {bulkTagAction === "add"
                ? "Selected tags will be added to each task."
                : "Selected tags will be removed from each task."}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeBulkTagsDialog}
              disabled={bulkActionSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                void handleBulkTagsApply();
              }}
              disabled={bulkActionSubmitting}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isBulkCollaboratorsDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeBulkCollaboratorsDialog();
            return;
          }
          setIsBulkCollaboratorsDialogOpen(true);
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Bulk Collaborators</DialogTitle>
            <DialogDescription>
              Update collaborators for {selectedTaskCount} selected tasks.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={
                  bulkCollaboratorAction === "add" ? "default" : "outline"
                }
                onClick={() => setBulkCollaboratorAction("add")}
                disabled={bulkActionSubmitting}
              >
                Add
              </Button>
              <Button
                type="button"
                variant={
                  bulkCollaboratorAction === "remove" ? "default" : "outline"
                }
                onClick={() => setBulkCollaboratorAction("remove")}
                disabled={bulkActionSubmitting}
              >
                Remove
              </Button>
            </div>
            <CollaboratorPicker
              collaborators={bulkCollaboratorSelections}
              handleFieldCollaborators={handleBulkCollaboratorToggle}
              collaboratorRef={bulkCollaboratorRef}
              portalContainer={bulkCollaboratorsDialogContainer}
              side="bottom"
              align="start"
              containerClassName="w-full"
            />
            <div className="text-xs text-muted-foreground">
              {bulkCollaboratorAction === "add"
                ? "Selected collaborators will be added to each task."
                : "Selected collaborators will be removed from each task."}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeBulkCollaboratorsDialog}
              disabled={bulkActionSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                void handleBulkCollaboratorsApply();
              }}
              disabled={bulkActionSubmitting}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isBulkAssigneeDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeBulkAssigneeDialog();
            return;
          }
          setIsBulkAssigneeDialogOpen(true);
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Bulk Assign</DialogTitle>
            <DialogDescription>
              Assign {selectedTaskCount} selected tasks to one assignee.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <AssigneePicker
              assignedTo={bulkAssignee}
              setAssignedTo={setBulkAssignee}
              handleFieldAssignedTo={(id) => setBulkAssignee(id)}
              modal={true}
              disabled={bulkActionSubmitting}
            />
            <div className="text-xs text-muted-foreground">
              Tasks will be assigned to the selected person.
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeBulkAssigneeDialog}
              disabled={bulkActionSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                void handleBulkAssigneeApply();
              }}
              disabled={bulkActionSubmitting}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isBulkDueDateDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeBulkDueDateDialog();
            return;
          }
          setIsBulkDueDateDialogOpen(true);
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Bulk Due Date</DialogTitle>
            <DialogDescription>
              Set or clear due dates for {selectedTaskCount} selected tasks.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={bulkDueDateAction === "set" ? "default" : "outline"}
                onClick={() => setBulkDueDateAction("set")}
                disabled={bulkActionSubmitting}
              >
                Set Date
              </Button>
              <Button
                type="button"
                variant={bulkDueDateAction === "clear" ? "default" : "outline"}
                onClick={() => setBulkDueDateAction("clear")}
                disabled={bulkActionSubmitting}
              >
                Clear Date
              </Button>
            </div>
            {bulkDueDateAction === "set" ? (
              <DueDatePicker
                dueDate={bulkDueDate}
                onDateChange={(date) => setBulkDueDate(date ?? null)}
                modal={true}
                disabled={bulkActionSubmitting}
              />
            ) : (
              <div className="text-sm text-muted-foreground">
                Due dates will be removed from each task.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeBulkDueDateDialog}
              disabled={bulkActionSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                void handleBulkDueDateApply();
              }}
              disabled={bulkActionSubmitting}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DeleteTaskDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onDelete={async () => {
          await handleBulkDeleteTasks();
        }}
        type="bulk"
      />
    </ResizablePanelGroup>
  );
};

export default AllTaskView;
